import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowLeft, CheckCircle } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, TextArea, useChatStream } from '@airia/ui';
import { Progress } from '../../ui/progress';
import { LoadingSpinner } from '../../ui/loading-spinner';
import ComparisonCard from './ComparisonCard';
import { Dialog, DialogDescription, DialogHeader, DialogTitle, DialogContent, } from '../../ui/dialog';
import { useAuth } from 'react-oidc-context';
import { getDocumentText, isInDocument, replaceText, searchText } from '../../lib/office';
import { jsonrepair } from 'jsonrepair';
import { getRequiredConfig } from '../../lib/service-discovery';
import { useUserId } from '../../lib/useUserId';
import { useBrowserAgentVariables } from '../McpSessionProvider';
import ChatDrawer from '../chat/ChatDrawer';
import Chat from '../chat/Chat';
const PlaybookDetails = ({ playbookDocComparisonDeploymentId, onBack, playbook, onComparisonPipelineExecution, }) => {
    const { user } = useAuth();
    const { apiUrl } = getRequiredConfig();
    const { userId } = useUserId();
    const browserAgentVariables = useBrowserAgentVariables();
    const [activeComparison, setActiveComparison] = React.useState(0);
    const [progress, setProgress] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [suggestedText, setSuggestedText] = React.useState('');
    const [chatDrawerOpen, setChatDrawerOpen] = React.useState(false);
    // Track which messages have been processed to prevent duplicate processing
    const processedMessageIds = useRef(new Set());
    // Track which playbook clauses have been submitted to prevent duplicate submissions
    const submittedPlaybookClauses = useRef(null);
    const chatStream = useChatStream({
        apiUrl,
        agentId: playbookDocComparisonDeploymentId,
        bearerToken: user?.access_token,
    });
    // Store submit in a ref to avoid dependency issues
    const submitRef = useRef(chatStream.submit);
    useEffect(() => {
        submitRef.current = chatStream.submit;
    }, [chatStream.submit]);
    // Map StreamMessage[] to the format expected by the component
    const allMessages = useMemo(() => {
        return chatStream.messages
            .filter((m) => m.message != null && m.message !== '')
            .map((m) => ({
            id: m.id,
            value: m.message,
            role: m.role,
        }));
    }, [chatStream.messages]);
    useEffect(() => {
        // Prevent duplicate submissions for the same playbook clauses
        // This guards against re-renders caused by unstable submit reference from useStream
        if (playbook.clauses.length > 0 &&
            userId &&
            submittedPlaybookClauses.current !== playbook.clauses) {
            submittedPlaybookClauses.current = playbook.clauses;
            setIsLoading(true);
            getDocumentText().then((text) => {
                submitRef
                    .current({
                    data: {
                        userInput: JSON.stringify({
                            playbook: playbook.clauses,
                            document: text,
                        }),
                        userId,
                        variables: browserAgentVariables,
                    },
                })
                    .finally(() => {
                    setIsLoading(false);
                });
            });
        }
        // Note: submit is intentionally excluded from dependencies as it may not be stable
        // We use submitRef to access the latest submit function
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playbook.clauses, userId]);
    useEffect(() => {
        setProgress((activeComparison / playbook.comparisons.length) * 100);
    }, [activeComparison, playbook.comparisons.length]);
    const onComparisonResult = useCallback(async (agentResult) => {
        let response;
        let matchingComparisons = [];
        try {
            response = JSON.parse(agentResult);
            for (let i = 0; i < response.length; i++) {
                const comparison = response[i];
                if (!comparison.original || typeof comparison.original !== 'string') {
                    continue;
                }
                const isInDoc = await isInDocument(comparison.original);
                if (isInDoc) {
                    matchingComparisons.push(comparison);
                }
            }
            onComparisonPipelineExecution(playbook.id, matchingComparisons);
        }
        catch (err) {
            // Sometimes LLM responses are not valid JSON
            if (err instanceof SyntaxError) {
                try {
                    const repaired = jsonrepair(agentResult);
                    response = JSON.parse(repaired);
                    matchingComparisons = [];
                    for (let i = 0; i < response.length; i++) {
                        const comparison = response[i];
                        if (!comparison.original || typeof comparison.original !== 'string') {
                            continue;
                        }
                        const isInDoc = await isInDocument(comparison.original);
                        if (isInDoc) {
                            matchingComparisons.push(comparison);
                        }
                    }
                    onComparisonPipelineExecution(playbook.id, matchingComparisons);
                }
                catch (repairErr) {
                    console.error('Failed to parse comparison response:', repairErr);
                    matchingComparisons = [];
                }
            }
            else {
                console.error('Error processing comparison response:', err);
            }
        }
        return matchingComparisons;
    }, [onComparisonPipelineExecution, playbook.id]);
    useEffect(() => {
        if (allMessages.length > 0 && !chatStream.isLoading) {
            const lastMessage = allMessages[allMessages.length - 1];
            // Skip if this message has already been processed
            if (processedMessageIds.current.has(lastMessage.id)) {
                return;
            }
            if (lastMessage.role === 'assistant') {
                // Mark as processed before async operation to prevent duplicate calls
                processedMessageIds.current.add(lastMessage.id);
                onComparisonResult(lastMessage.value)
                    .then((comparisons) => {
                    if (comparisons && comparisons.length > 0) {
                        setProgress((activeComparison / comparisons.length) * 100);
                        setActiveComparison(0);
                    }
                })
                    .catch((err) => {
                    console.error('Error in onComparisonResult:', err);
                    // Remove from processed set on error so it can be retried
                    processedMessageIds.current.delete(lastMessage.id);
                });
            }
        }
    }, [allMessages, chatStream.isLoading, activeComparison, onComparisonResult]);
    const onApplyClick = (suggestedText) => {
        setSuggestedText(suggestedText);
        setDialogOpen(true);
    };
    const onApplyAllClick = async () => {
        try {
            for (let i = activeComparison; i < playbook.comparisons.length; i++) {
                const comparison = playbook.comparisons[i];
                await replaceText(comparison.original, comparison.suggested);
            }
            setSuggestedText('');
            setActiveComparison(-1);
        }
        catch (err) {
            console.error('Error replacing text in document', err);
        }
    };
    const onConfirmClick = async () => {
        setSuggestedText('');
        setDialogOpen(false);
        await replaceText(playbook.comparisons[activeComparison].original, suggestedText);
        if (activeComparison + 1 < playbook.comparisons.length) {
            await searchText(playbook.comparisons[activeComparison + 1].original);
        }
        setActiveComparison((prev) => (prev + 1 >= playbook.comparisons.length ? -1 : prev + 1));
    };
    return (_jsxs("div", { className: 'w-full h-full flex flex-col bg-background px-2', children: [_jsx("div", { className: "flex-1 min-h-0 overflow-y-auto mt-4", children: _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsxs("div", { className: 'flex items-center justify-between', children: [_jsxs("div", { className: 'flex items-center gap-4', children: [_jsx(Button, { variant: 'link', onClick: () => onBack(), children: _jsx(ArrowLeft, {}) }), _jsx("h1", { className: 'text-xl', children: playbook.name })] }), _jsx(Button, { className: 'rounded-md', onClick: onApplyAllClick, disabled: activeComparison < 0 && playbook.comparisons.length > 0, children: "Apply All Changes" })] }), _jsx(Progress, { value: progress }), activeComparison < 0 && (_jsxs("div", { className: 'flex flex-col gap-4 justify-center items-center py-4', children: [_jsx("span", { className: 'text-xl', children: "All Changes Addressed" }), _jsx(CheckCircle, { className: 'size-10 text-success' })] })), activeComparison >= 0 && (_jsx("div", { className: 'flex flex-col gap-4', children: isLoading ? (_jsx("div", { className: 'flex justify-center items-center my-2', children: _jsx(LoadingSpinner, {}) })) : (playbook.comparisons.map((comparison, idx) => (_jsx(ComparisonCard, { comparison: comparison, index: idx, active: idx === activeComparison, totalCount: playbook.comparisons.length, onApplyChanges: onApplyClick, onDismiss: async () => {
                                    if (activeComparison + 1 <
                                        playbook.comparisons.length) {
                                        await searchText(playbook.comparisons[activeComparison + 1]
                                            .original);
                                    }
                                    setActiveComparison(activeComparison + 1);
                                } }, `comparison-${idx}`)))) }))] }) }), _jsx("div", { className: "flex-shrink-0", children: _jsx(ChatDrawer, { open: chatDrawerOpen, onOpenChange: setChatDrawerOpen, children: _jsx(Chat, {}) }) }), _jsx(Dialog, { open: dialogOpen, onOpenChange: setDialogOpen, children: _jsxs(DialogContent, { className: 'bg-card rounded-lg', children: [_jsx(DialogHeader, { children: _jsx(DialogTitle, { children: "Apply Changes" }) }), _jsx(DialogDescription, { children: "Are you sure you want to apply changes?" }), _jsx(TextArea, { value: suggestedText, onChange: (e) => setSuggestedText(e.target.value) }), _jsxs("div", { className: 'flex gap-2', children: [_jsx(Button, { id: 'confirmBtn', className: 'rounded-md', onClick: () => {
                                        onConfirmClick();
                                    }, children: "Apply" }), _jsx(Button, { id: 'cancelBtn', onClick: () => setDialogOpen(false), variant: 'outline', children: "Cancel" })] })] }) })] }));
};
export default PlaybookDetails;
